import { useQuery } from '@tanstack/react-query'
import { Clock, Globe, Mail, MapPin, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { schoolQuery } from '@/features/school/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { intlLocale } from '@/i18n'
import { cn } from '@/utils/cn'

/** Localised weekday name for a 0-based (Sunday first) day of week. */
function weekdayName(day: number): string {
  // 2023-01-01 was a Sunday.
  const name = new Intl.DateTimeFormat(intlLocale(), { weekday: 'long' }).format(new Date(2023, 0, 1 + day))
  return name.charAt(0).toUpperCase() + name.slice(1)
}

export function FamilySchoolPage() {
  const { t } = useTranslation('portal')
  useDocumentTitle('School')
  const { data, isPending, error, refetch } = useQuery(schoolQuery())
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  const today = new Date().getDay()

  return (
    <>
      <PageHeader
        title={data.name}
        description={data.motto ?? t('Contact details and opening hours.')}
        breadcrumbs={[{ label: t('School') }]}
        homeHref="/portal"
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('Contact the school')} />
          <CardBody className="space-y-4 text-sm">
            {data.phone ? (
              <a
                href={`tel:${data.phone}`}
                className="flex items-center gap-3 text-ink-soft hover:text-brand-700"
              >
                <Phone className="size-4 text-brand-600" aria-hidden="true" />
                {data.phone}
              </a>
            ) : null}
            {data.email ? (
              <a
                href={`mailto:${data.email}`}
                className="flex items-center gap-3 text-ink-soft hover:text-brand-700"
              >
                <Mail className="size-4 text-brand-600" aria-hidden="true" />
                {data.email}
              </a>
            ) : null}
            {data.website ? (
              <a
                href={data.website}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-ink-soft hover:text-brand-700"
              >
                <Globe className="size-4 text-brand-600" aria-hidden="true" />
                {data.website}
              </a>
            ) : null}
            {data.address ? (
              <p className="flex items-start gap-3 whitespace-pre-line text-ink-soft">
                <MapPin className="mt-0.5 size-4 text-brand-600" aria-hidden="true" />
                {data.address}
              </p>
            ) : null}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t('Opening hours')} icon={<Clock />} />
          <ul className="divide-y divide-line">
            {data.openingHours.map((h) => (
              <li
                key={h.dayOfWeek}
                className={cn(
                  'flex justify-between px-5 py-2.5 text-sm',
                  h.dayOfWeek === today && 'bg-brand-50/50 font-medium',
                )}
              >
                <span className="text-ink-soft">
                  {weekdayName(h.dayOfWeek)}
                  {h.dayOfWeek === today ? ` (${t('today')})` : ''}
                </span>
                <span className={h.isOpen ? 'text-ink tabular' : 'text-subtle'}>
                  {h.isOpen ? `${h.openTime ?? ''} – ${h.closeTime ?? ''}` : t('Closed')}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  )
}
